/**
 * @fileoverview Calendar Helper Utilities
 * @module features/coordinator/planner/utils/calendarHelpers
 * @description Utility functions for calendar view operations including date manipulation, formatting, and room grouping
 * @created 10-10-2025 20:53:20
 * @author MCHD Room Scheduler Team
 */

import { format, startOfWeek, endOfWeek, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, addWeeks, isPast, startOfDay } from 'date-fns';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Session template structure
 */
export interface SessionTemplate {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  bufferMinutes?: number;
}

/**
 * Room structure with floor and building information
 */
export interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  status: string;
  floorId: string;
  floor: {
    id: string;
    name: string;
    level: number;
    buildingId: string;
    building: {
      id: string;
      name: string;
      code: string;
    };
  };
}

/**
 * Assignment structure
 */
export interface Assignment {
  id: string;
  title: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  published: boolean;
  // FIXED: 15-10-2025 19:30:00 - Add nested data from backend API
  user?: {
    id: string;
    name: string;
    role: string;
  };
  room?: {
    id: string;
    number: string;
    name: string;
  };
  sessionTime?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  // Legacy support
  users?: {
    id: string;
    name: string;
  };
}

export interface CellStatus {
  roomId: string;
  sessionTemplateId: string;
  date: string;
  status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  assignment?: Assignment;
  conflicts?: string[];
  holiday?: any;
}

// =============================================================================
// DATE MANIPULATION FUNCTIONS
// =============================================================================

/**
 * Get array of dates for a week starting from a given date
 * @param startDate - Start date (defaults to start of current week)
 * @returns Array of 7 Date objects representing the week
 * @example
 * const weekDates = getWeekDates(new Date('2025-10-10'));
 * // Returns [Mon Oct 6, Tue Oct 7, ..., Sun Oct 12]
 */
export function getWeekDates(startDate: Date = new Date()): Date[] {
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 }); // Monday start
  const weekEnd = endOfWeek(startDate, { weekStartsOn: 1 });
  
  return eachDayOfInterval({ start: weekStart, end: weekEnd });
}

/**
 * Get array of dates for a month
 * @param monthStart - Start date of the month
 * @returns Array of Date objects representing all days in the month including padding days
 * @example
 * const monthDates = getMonthDates(new Date('2025-10-01'));
 * // Returns all dates in October 2025 plus padding days
 */
export function getMonthDates(monthStart: Date): Date[] {
  const monthStartDate = startOfMonth(monthStart);
  const monthEndDate = endOfMonth(monthStart);
  
  // Get padding days from previous month to start on Monday
  const calendarStart = startOfWeek(monthStartDate, { weekStartsOn: 1 });
  
  // Get padding days from next month to end on Sunday
  const calendarEnd = endOfWeek(monthEndDate, { weekStartsOn: 1 });
  
  return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
}

/**
 * Navigate to next period (day/week/month)
 * @param currentDate - Current date
 * @param viewMode - View mode ('day' | 'week' | 'month')
 * @returns New date for next period
 */
export function navigateNext(currentDate: Date, viewMode: 'day' | 'week' | 'month'): Date {
  switch (viewMode) {
    case 'day':
      return addDays(currentDate, 1);
    case 'week':
      return addWeeks(currentDate, 1);
    case 'month':
      return addMonths(currentDate, 1);
    default:
      return currentDate;
  }
}

/**
 * Navigate to previous period (day/week/month)
 * @param currentDate - Current date
 * @param viewMode - View mode ('day' | 'week' | 'month')
 * @returns New date for previous period
 */
export function navigatePrevious(currentDate: Date, viewMode: 'day' | 'week' | 'month'): Date {
  switch (viewMode) {
    case 'day':
      return addDays(currentDate, -1);
    case 'week':
      return addWeeks(currentDate, -1);
    case 'month':
      return addMonths(currentDate, -1);
    default:
      return currentDate;
  }
}

/**
 * Get today's date at midnight (for comparison purposes)
 * @returns Date object set to today at 00:00:00
 */
export function getTodayMidnight(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

// =============================================================================
// DATE FORMATTING FUNCTIONS
// =============================================================================

/**
 * Format session time for display
 * @param session - Session template object
 * @returns Formatted time string (e.g., "08:00 AM - 12:00 PM")
 */
export function formatSessionTime(session: SessionTemplate): string {
  try {
    const start = format(new Date(`2000-01-01T${session.startTime}`), 'hh:mm a');
    const end = format(new Date(`2000-01-01T${session.endTime}`), 'hh:mm a');
    return `${start} - ${end}`;
  } catch (error) {
    return `${session.startTime} - ${session.endTime}`;
  }
}

/**
 * Format date for display in calendar header
 * @param date - Date to format
 * @param viewMode - View mode to determine formatting style
 * @returns Formatted date string
 */
export function formatCalendarHeader(date: Date, viewMode: 'day' | 'week' | 'month'): string {
  switch (viewMode) {
    case 'day':
      return format(date, 'EEEE, MMMM d, yyyy'); // "Monday, October 10, 2025"
    case 'week':
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`; // "Oct 6 - Oct 12, 2025"
    case 'month':
      return format(date, 'MMMM yyyy'); // "October 2025"
    default:
      return format(date, 'PPP');
  }
}

/**
 * Format date for short display (e.g., day column header)
 * @param date - Date to format
 * @returns Short formatted date string
 */
export function formatDayShort(date: Date): string {
  return format(date, 'EEE d'); // "Mon 10"
}

/**
 * Format time in 12-hour format
 * @param timeString - Time string in HH:mm format
 * @returns Formatted time (e.g., "08:00 AM")
 */
export function formatTime12Hour(timeString: string): string {
  try {
    return format(new Date(`2000-01-01T${timeString}`), 'hh:mm a');
  } catch (error) {
    return timeString;
  }
}

/**
 * Enrich cell status assignment with full data from assignments array
 * Matches the pattern used by EnhancedPlannerGrid
 * @param cellStatus - Cell status with limited assignment data
 * @param assignments - Full assignments array with nested data
 * @returns Enriched assignment with room, sessionTime, and user data
 * @created 15-10-2025 19:08:10
 */
export function enrichCellStatusAssignment(
  cellStatus: CellStatus,
  assignments: Assignment[]
): CellStatus {
  if (!cellStatus.assignment) {
    return cellStatus;
  }

  // Find full assignment data by matching roomId, sessionTemplateId, and date
  const fullAssignment = assignments.find(
    a => a.roomId === cellStatus.roomId &&
         a.sessionTemplateId === cellStatus.sessionTemplateId &&
         a.date.startsWith(cellStatus.date) // Match date part only
  );

  if (fullAssignment) {
    // Replace limited assignment data with full data
    return {
      ...cellStatus,
      assignment: fullAssignment
    };
  }

  return cellStatus;
}

// =============================================================================
// DATE COMPARISON FUNCTIONS
// =============================================================================

/**
 * Check if a date is today
 * @param date - Date to check
 * @returns True if date is today
 */
export function isDateToday(date: Date): boolean {
  return isToday(date);
}

/**
 * Check if a date is in the past
 * Uses date-fns isPast for accurate comparison
 * @param date - Date to check
 * @returns True if date is before today
 */
export function isDatePast(date: Date): boolean {
  return isPast(startOfDay(date));
}

/**
 * Check if a date is in the future
 * @param date - Date to check
 * @returns True if date is after today
 */
export function isDateFuture(date: Date): boolean {
  const today = getTodayMidnight();
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate > today;
}

/**
 * Check if two dates are the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if dates are on the same day
 */
export function isSameDate(date1: Date, date2: Date): boolean {
  return isSameDay(date1, date2);
}

// =============================================================================
// ROOM GROUPING FUNCTIONS
// =============================================================================

/**
 * Group rooms by floor
 * @param rooms - Array of room objects
 * @returns Object mapping floor IDs to arrays of rooms
 */
export function groupRoomsByFloor(rooms: Room[]): Record<string, Room[]> {
  return rooms.reduce((acc, room) => {
    const floorId = room.floorId;
    if (!acc[floorId]) {
      acc[floorId] = [];
    }
    acc[floorId].push(room);
    return acc;
  }, {} as Record<string, Room[]>);
}

/**
 * Group rooms by building
 * @param rooms - Array of room objects
 * @returns Object mapping building IDs to arrays of rooms
 */
export function groupRoomsByBuilding(rooms: Room[]): Record<string, Room[]> {
  return rooms.reduce((acc, room) => {
    const buildingId = room.floor.buildingId;
    if (!acc[buildingId]) {
      acc[buildingId] = [];
    }
    acc[buildingId].push(room);
    return acc;
  }, {} as Record<string, Room[]>);
}

/**
 * Sort rooms by floor level and room number
 * @param rooms - Array of room objects
 * @returns Sorted array of rooms
 */
export function sortRooms(rooms: Room[]): Room[] {
  return [...rooms].sort((a, b) => {
    // First sort by floor level
    if (a.floor.level !== b.floor.level) {
      return a.floor.level - b.floor.level;
    }
    // Then sort by room number
    return a.number.localeCompare(b.number, undefined, { numeric: true });
  });
}

// =============================================================================
// OCCUPANCY CALCULATION FUNCTIONS
// =============================================================================

/**
 * Calculate room utilization percentage
 * @param assignments - Array of assignments for the room
 * @param totalSlots - Total number of available slots
 * @returns Utilization percentage (0-100)
 */
export function calculateUtilization(assignments: Assignment[], totalSlots: number): number {
  if (totalSlots === 0) return 0;
  const occupiedSlots = assignments.filter(a => a.status === 'CONFIRMED').length;
  return Math.round((occupiedSlots / totalSlots) * 100);
}

/**
 * Get color based on utilization percentage
 * @param utilization - Utilization percentage (0-100)
 * @returns Mantine color name
 */
export function getOccupancyColor(utilization: number): string {
  if (utilization >= 90) return 'red';
  if (utilization >= 70) return 'orange';
  if (utilization >= 50) return 'yellow';
  if (utilization >= 25) return 'blue';
  return 'green';
}

/**
 * Get status indicator for occupancy
 * @param utilization - Utilization percentage (0-100)
 * @returns Status object with color and label
 */
export function getOccupancyStatus(utilization: number): { color: string; label: string } {
  if (utilization >= 90) return { color: 'red', label: 'Critical' };
  if (utilization >= 70) return { color: 'orange', label: 'High' };
  if (utilization >= 50) return { color: 'yellow', label: 'Moderate' };
  if (utilization >= 25) return { color: 'blue', label: 'Low' };
  return { color: 'green', label: 'Available' };
}

// =============================================================================
// DATE RANGE FUNCTIONS
// =============================================================================

/**
 * Get date range for API queries based on view mode
 * @param currentDate - Current viewing date
 * @param viewMode - View mode ('day' | 'week' | 'month')
 * @returns Object with startDate and endDate as ISO strings
 */
export function getDateRangeForView(currentDate: Date, viewMode: 'day' | 'week' | 'month'): {
  startDate: string;
  endDate: string;
} {
  let start: Date;
  let end: Date;

  switch (viewMode) {
    case 'day':
      start = new Date(currentDate);
      start.setHours(0, 0, 0, 0);
      end = new Date(currentDate);
      end.setHours(23, 59, 59, 999);
      break;
    case 'week':
      start = startOfWeek(currentDate, { weekStartsOn: 1 });
      end = endOfWeek(currentDate, { weekStartsOn: 1 });
      break;
    case 'month':
      start = startOfMonth(currentDate);
      end = endOfMonth(currentDate);
      break;
    default:
      start = new Date(currentDate);
      end = new Date(currentDate);
  }

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  // Date manipulation
  getWeekDates,
  getMonthDates,
  navigateNext,
  navigatePrevious,
  getTodayMidnight,
  
  // Formatting
  formatSessionTime,
  formatCalendarHeader,
  formatDayShort,
  formatTime12Hour,
  
  // Date comparison
  isDateToday,
  isDatePast,
  isDateFuture,
  isSameDate,
  
  // Room grouping
  groupRoomsByFloor,
  groupRoomsByBuilding,
  sortRooms,
  
  // Occupancy
  calculateUtilization,
  getOccupancyColor,
  getOccupancyStatus,
  
  // Date ranges
  getDateRangeForView
};

